import React, { useEffect, useState, useRef, ChangeEvent } from "react"

import useMediaQuery from "@material-ui/core/useMediaQuery"
import SliderMUI from "@material-ui/core/Slider"

import { NumericFormat } from "react-number-format"
import { EditIcon } from "../icons"

import { convertStringValueToNumber } from "../helpers/formatters"
import { DLClickEvent, TDLButtonsVariant } from "../../../helpers/WatcherDL/DLEvents/DLClickEvent"
import { formatNumber, formatNumberForPlaceholder } from "../helpers"
import * as styles from "./Slider.module.scss"

type Props = {
  title: string
  value: number
  setValue: (value: number) => void
  maxValidatedValue?: number
  min: number
  max: number
  step: number
  name: TDLButtonsVariant
}

const MAX_INPUT_VALUE = 999_999_999

const Slider = ({
  title,
  value,
  maxValidatedValue = MAX_INPUT_VALUE,
  min,
  max,
  step,
  setValue,
  name,
}: Props) => {
  const isMobTab = useMediaQuery("(max-width:959.5px)")
  const inputRef = useRef<HTMLInputElement>(null)
  const pushInDataLayer = () => {
    DLClickEvent({ name, placement: "calculator" })
  }

  const [inputValue, setInputValue] = useState(value)

  const handleChangeInput = (e: { currentTarget: { value: string } }) => {
    if (e.currentTarget.value === "") return
    const newValue = convertStringValueToNumber(e.currentTarget.value)
    setInputValue(newValue)
    pushInDataLayer()
  }

  const handleChangeSlider = (_: ChangeEvent<{}>, sliderValue: number | number[]) => {
    setInputValue(sliderValue as number)
    pushInDataLayer()
  }

  const handleBlurInput = () => {
    if (inputValue < min) {
      setInputValue(min)
    }
    if (inputValue > max) {
      setInputValue(max)
    }
  }

  const clickEditBtn = () => {
    if (inputRef?.current) {
      inputRef.current.focus()
    }
  }

  useEffect(() => {
    if (inputValue < min) {
      setValue(min)
      return
    }
    if (inputValue > max) {
      setValue(max)
      return
    }
    setValue(inputValue)
  }, [inputValue, max, min, setValue])

  return (
    <div className={styles.container}>
      <div className={styles.sliderWrapper}>
        <label htmlFor={title} className={styles.title}>
          {title}
        </label>
        <div className={styles.valueContainer}>
          <NumericFormat
            getInputRef={inputRef}
            id={title}
            className={styles.inputNumericFormat}
            placeholder={`${formatNumberForPlaceholder(min)} ₽`}
            allowNegative={false}
            suffix=" ₽"
            thousandsGroupStyle="thousand"
            thousandSeparator=" "
            value={inputValue}
            onChange={handleChangeInput}
            isAllowed={(values) => {
              const { floatValue } = values
              if (floatValue === undefined) return true
              return !(floatValue > maxValidatedValue)
            }}
            onBlur={handleBlurInput}
            decimalScale={0}
          />
        </div>
        <div className={styles.sliderContainer}>
          <SliderMUI
            value={inputValue}
            onChange={handleChangeSlider}
            min={min}
            max={max}
            step={step}
            classes={{
              root: styles.root,
              track: styles.track,
              rail: styles.rail,
              thumb: styles.thumb,
            }}
          />
        </div>
        <EditIcon onClick={clickEditBtn} className={styles.editIcon} smallSizeIcon={isMobTab} />
      </div>
      <div className={styles.boundaryValues}>
        <span>{formatNumber(min)}</span>
        <span>{formatNumber(max)}</span>
      </div>
    </div>
  )
}

export default Slider
